import React, { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../supabaseClient';
import primaryLogo from '../assets/logo/syncehs-lockup-black-v2.svg';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

// Stripe price configuration
const PRICING_PLANS = {
  monthly: {
    priceId: 'price_1Sego7BCcNMrUAO5q5yCfkJo',
    price: 69,
    interval: 'monthly'
  },
  annual: {
    priceId: 'price_1SegqHBCcNMrUAO5lIjhiLQP',
    price: 708,
    interval: 'yearly'
  }
};

const GetStarted: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');
  const [userCount, setUserCount] = useState(1);
  // SyncConnect invitation token from the email CTA (?connect_token=...). Captured once on
  // mount and passed through to checkout metadata; a later phase completes the acceptance.
  const [connectToken] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('connect_token')
  );
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate pricing
  const pricing = useMemo(() => {
    const plan = PRICING_PLANS[selectedPlan];
    const total = plan.price * userCount;
    const monthlyEquivalent = selectedPlan === 'annual' ? Math.round(total / 12) : total;
    const annualTotal = selectedPlan === 'monthly' ? plan.price * userCount * 12 : total;
    const savings = selectedPlan === 'annual' ? (69 * userCount * 12) - total : 0;
    
    return {
      total,
      monthlyEquivalent,
      annualTotal,
      savings,
      unit: plan.price,
      interval: plan.interval
    };
  }, [selectedPlan, userCount]);

  const handleCheckout = async () => {
    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: PRICING_PLANS[selectedPlan].priceId,
          quantity: userCount,
          companyName: companyName.trim(),
          email: email.trim(),
          planType: 'standard',
          ...(connectToken ? { connect_token: connectToken } : {})
        },
      });

      if (functionError) {
        throw functionError;
      }

      const { sessionId } = data;
      const stripe = await stripePromise;
      if (stripe) {
        const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });
        if (stripeError) {
          throw stripeError;
        }
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      setError(error.message || 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-bg">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={primaryLogo} 
            alt="SyncEHS" 
            className="h-auto w-60 max-w-full mx-auto mb-6"
          />
          <h1 className="font-display tracking-tight text-3xl font-bold text-text-primary mb-2">
            Get Started with SyncEHS
          </h1>
          <p className="text-lg text-text-body">
            One safety platform. Admin seats. Unlimited employees.
          </p>
          <div className="mt-5 inline-flex max-w-full items-start gap-3 rounded-lg border border-accent-blue/20 bg-accent-blue-wash px-4 py-3 text-left">
            <svg aria-hidden="true" className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
              <circle cx="12" cy="12" r="8.25" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5V12l3 1.75" />
            </svg>
            <div>
              <p className="text-base font-semibold text-text-primary">Guided setup in about 15 minutes.</p>
              <p className="mt-1 text-sm text-text-body">A step-by-step guide tailored to your business.</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-card rounded-xl shadow-lg p-5 sm:p-8">
          {/* Plan Selection */}
          <div className="mb-8">
            <h2 className="font-display text-xl font-semibold text-text-primary mb-4">Choose Your Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Monthly Plan */}
              <div
                className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedPlan === 'monthly'
                    ? 'border-accent-blue bg-accent-blue-wash'
                    : 'border-surface-border hover:border-surface-border'
                }`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-lg font-medium text-text-primary">Monthly</h3>
                    <p className="text-sm text-text-muted">Monthly billing. Cancel anytime.</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-text-primary">$69</div>
                    <div className="text-sm text-text-muted">per admin seat/month</div>
                  </div>
                </div>
                <div className="mt-4">
                  <input
                    type="radio"
                    name="plan"
                    checked={selectedPlan === 'monthly'}
                    onChange={() => setSelectedPlan('monthly')}
                    className="h-4 w-4 text-accent-blue focus:ring-accent-blue border-surface-border"
                  />
                </div>
              </div>

              {/* Annual Plan */}
              <div
                className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedPlan === 'annual'
                    ? 'border-accent-blue bg-accent-blue-wash'
                    : 'border-surface-border hover:border-surface-border'
                }`}
                onClick={() => setSelectedPlan('annual')}
              >
                <div className="absolute top-0 right-0 bg-status-success text-white px-3 py-1 text-sm font-medium rounded-bl-lg rounded-tr-lg">
                  Save 15%
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-lg font-medium text-text-primary">Annual</h3>
                    <p className="text-sm text-text-muted">Best value - pay yearly</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-text-primary">$708</div>
                    <div className="text-sm text-text-muted">per admin seat/year</div>
                    <div className="text-xs text-status-success">~$59/admin seat/month</div>
                  </div>
                </div>
                <div className="mt-4">
                  <input
                    type="radio"
                    name="plan"
                    checked={selectedPlan === 'annual'}
                    onChange={() => setSelectedPlan('annual')}
                    className="h-4 w-4 text-accent-blue focus:ring-accent-blue border-surface-border"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* User Count Selector */}
          <div className="mb-8">
            <label htmlFor="userCount" className="block text-sm font-medium text-text-body mb-2">
              Admin seats
            </label>
            <select
              id="userCount"
              value={userCount}
              onChange={(e) => setUserCount(parseInt(e.target.value))}
              className="block w-full px-3 py-2 border border-surface-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue sm:text-sm"
            >
              {Array.from({ length: 50 }, (_, i) => i + 1).map((num) => (
                <option key={num} value={num}>
                  {num} admin seat{num > 1 ? 's' : ''}
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-text-body">
              Seats are for people who manage your safety program. Employees participate through QR codes at no extra cost.
            </p>
          </div>

          {/* Dynamic Pricing Display */}
          <div className="mb-8 p-6 bg-surface-bg rounded-lg">
            <h3 className="font-display text-lg font-semibold text-text-primary mb-3">Pricing Summary</h3>
            <div className="space-y-2">
              {selectedPlan === 'monthly' ? (
                <>
                  <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm">
                    <span>{userCount} admin seat{userCount > 1 ? 's' : ''} × ${pricing.unit}/month</span>
                    <span className="font-medium whitespace-nowrap">${pricing.total}/month</span>
                  </div>
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>Annual equivalent</span>
                    <span>${pricing.annualTotal}/year</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm">
                    <span>{userCount} admin seat{userCount > 1 ? 's' : ''} × ${pricing.unit}/year</span>
                    <span className="font-medium whitespace-nowrap">${pricing.total}/year</span>
                  </div>
                  <div className="flex justify-between text-sm text-status-success">
                    <span>You save</span>
                    <span className="font-medium">${pricing.savings}/year</span>
                  </div>
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>Monthly equivalent</span>
                    <span>~${pricing.monthlyEquivalent}/month</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Company Information */}
          <div className="mb-8">
            <h3 className="font-display text-lg font-semibold text-text-primary mb-4">Company Information</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-text-body mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter your company name"
                  className="block w-full px-3 py-2 border border-surface-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue sm:text-sm"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-body mb-1">
                  Admin Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@company.com"
                  className="block w-full px-3 py-2 border border-surface-border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue sm:text-sm"
                  required
                />
                <p className="mt-1 text-xs text-text-muted">
                  We'll send setup instructions to this email
                </p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-status-danger-light border border-status-danger/20 rounded-md">
              <p className="text-sm text-status-danger">{error}</p>
            </div>
          )}

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={loading || !companyName.trim() || !email.trim()}
            className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              `Start Subscription - $${pricing.total}${selectedPlan === 'monthly' ? '/month' : '/year'}`
            )}
          </button>

          {/* Features List */}
          <div className="mt-8 pt-8 border-t border-surface-border">
            <h4 className="text-sm font-medium text-text-primary mb-4">What's included:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                'Incident tracking & reporting',
                'OSHA 300 & 300A records',
                'Training tracking & expiration alerts',
                'Safety audits & inspections',
                'Corrective action tracking',
                'JSAs & safety meetings',
                'Dashboards & reporting',
                'Designated medical providers by jobsite',
                'Worker QR access to injury care contacts',
                'Printable jobsite QR posters',
                'Unlimited employees through QR codes',
                'Email support'
              ].map((feature) => (
                <div key={feature} className="flex items-center text-sm text-text-body">
                  <svg className="h-4 w-4 text-status-success mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-surface-bg rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-text-muted mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <p className="text-sm text-text-body">
                  <strong>Secure Payment:</strong> Your payment is processed securely by Stripe. 
                  We never store your credit card information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetStarted;
